import axios from 'axios';
import api from '@src/api';
import cloneDeep from 'lodash/cloneDeep';

export const state = {
  allCircuits: [],
  circuitModel: null,
  circuitDetails: {},
  selectedCircuit: null,
  newCircuit: [],
  isFiltering: false,
  initAllCircuits: [],
  hasUnsavedCircuitChanges: false,
  goToCircuit: null,
};

export const getters = {
  hasUnsavedCircuitChanges(state) {
    return state.hasUnsavedCircuitChanges;
  },
};

export const mutations = {
  setInitAllCircuits(state, value) {
    state.initAllCircuits = value;
  },
  setAllCircuits(state, value) {
    state.allCircuits = value;
  },
  setHasUnsavedCircuitChanges(state, value) {
    state.hasUnsavedCircuitChanges = value;
  },
  setCreateCircuit(state, value) {
    state.allCircuits.push(value);

    if (state.isFiltering) {
      state.initAllCircuits.push(value);
    }
  },
  setCircuitDetails(state, value) {
    const circuitClone = cloneDeep(state.circuitDetails);
    circuitClone[value._id] = value;
    state.circuitDetails = circuitClone;
  },
  setUpdateCircuitItem(state, value) {
    const initCircuitsClone = cloneDeep(state.initAllCircuits);
    const j = initCircuitsClone.findIndex(
      (circuit) => circuit._id === value._id
    );
    initCircuitsClone[j] = value;
    state.initAllCircuits = initCircuitsClone;
  },
  setDeleteCircuitItem(state, value) {
    const i = state.allCircuits.findIndex((item) => item._id === value._id);
    state.allCircuits.splice(i, 1);

    if (state.isFiltering) {
      const j = state.initAllCircuits.findIndex(
        (item) => item._id === value._id
      );
      state.initAllCircuits.splice(j, 1);
    }
  },
  setSelectedCircuit(state, value) {
    state.selectedCircuit = value;
  },
  setAddCircuitExercise(state, value) {
    const circuitClone = cloneDeep(state.selectedCircuit);
    circuitClone.exercises.push(value);
    state.selectedCircuit = circuitClone;
  },
  setRemoveCircuitExercise(state, value) {
    const circuitClone = cloneDeep(state.selectedCircuit);
    circuitClone.exercises.splice(value, 1);
    state.selectedCircuit = circuitClone;
  },
  setMoveCircuitExerciseUp(state, value) {
    const circuitClone = cloneDeep(state.selectedCircuit);
    const prevIndex = value.index - 1;
    state.selectedCircuit.exercises = swapExercises(
      circuitClone.exercises,
      value.index,
      prevIndex
    );
  },
  setMoveCircuitExerciseDown(state, value) {
    const circuitClone = cloneDeep(state.selectedCircuit);
    const nextIndex = value.index + 1;
    state.selectedCircuit.exercises = swapExercises(
      circuitClone.exercises,
      value.index,
      nextIndex
    );
  },
  setUpdateExerciseSet(state, value) {
    const circuitClone = cloneDeep(state.selectedCircuit);
    const sets = circuitClone.exercises[value.exerciseIndex].setDirections;
    value.type === 'add' ? sets.push(value.set) : sets.pop();
    state.selectedCircuit = circuitClone;
  },
  setUpdateExerciseSetReps(state, value) {
    const circuitClone = cloneDeep(state.selectedCircuit);
    const setDirections =
      circuitClone.exercises[value.exerciseIndex].setDirections[
        value.exerciseSetIndex
      ];
    setDirections.reps = value.count;
    state.selectedCircuit = circuitClone;
  },
  setUpdateExerciseSetTime(state, value) {
    const circuitClone = cloneDeep(state.selectedCircuit);
    const set =
      circuitClone.exercises[value.exerciseIndex].setDirections[
        value.exerciseSetIndex
      ];
    set.time = value.count;
    state.selectedCircuit = circuitClone;
  },
  setUpdateExerciseSetWeight(state, value) {
    const circuitClone = cloneDeep(state.selectedCircuit);
    const set =
      circuitClone.exercises[value.exerciseIndex].setDirections[
        value.exerciseSetIndex
      ];
    set.weight = value.count;
    state.selectedCircuit = circuitClone;
  },
  setUpdateExerciseSetRest(state, value) {
    const circuitClone = cloneDeep(state.selectedCircuit);
    const set =
      circuitClone.exercises[value.exerciseIndex].setDirections[
        value.exerciseSetIndex
      ];
    set.afterSetRest = value.count;
    state.selectedCircuit = circuitClone;
  },
  setCircuitModel(state, value) {
    state.circuitModel = value;
  },
  setGoToCircuit(state, value) {
    state.goToCircuit = value;
  },
  toggleNewCircuitPlaceholder(state, value) {
    value && !state.newCircuit.length
      ? state.newCircuit.push(value)
      : state.newCircuit.pop();
  },
  toggleIsFiltering(state) {
    state.isFiltering = !state.isFiltering;
  },
};

export const actions = {
  getAllCircuits(ctx, { orgId, trainerId }) {
    if (orgId !== undefined && trainerId !== undefined) {
      const url = api.URL_GET_ALL_CIRCUITS.replace(':orgID', orgId).replace(
        ':trainerID',
        trainerId
      );

      return axios
        .get(url)
        .then((res) => {
          ctx.commit('setAllCircuits', res.data.programCircuits);
          ctx.commit('setInitAllCircuits', res.data.programCircuits);
          return res.data;
        })
        .catch((err) => {
          console.warn(err);
        });
    } else {
      console.error(
        `invalid params (orgId, trainerId) : (${orgId}, ${trainerId})`
      );
    }
  },

  getCircuit(ctx, { circuitId, orgId, trainerId }) {
    if (
      circuitId !== undefined &&
      orgId !== undefined &&
      trainerId !== undefined
    ) {
      if (!ctx.state.circuitDetails[circuitId]) {
        const url = api.URL_GET_CIRCUIT.replace(':circuitID', circuitId)
          .replace(':orgID', orgId)
          .replace(':trainerID', trainerId);

        return axios
          .get(url)
          .then((res) => {
            ctx.commit('setCircuitDetails', res.data.programCircuit);
            return res.data;
          })
          .catch((err) => {
            console.warn(err);
          });
      } else {
        return ctx.state.circuitDetails[circuitId];
      }
    } else {
      console.error(
        `invalid params (circuitId, orgId, trainerId) : (${circuitId}, ${orgId}, ${trainerId})`
      );
    }
  },

  createCircuit(ctx, { circuitObj }) {
    if (
      circuitObj !== undefined ||
      (circuitObj.trainer_id !== undefined &&
        circuitObj.org_id !== undefined &&
        circuitObj.user_id !== undefined)
    ) {
      const url = api.URL_CREATE_CIRCUIT.replace(':orgID', circuitObj.org_id)
        .replace(':trainerID', circuitObj.trainer_id)
        .replace(':userID', circuitObj.user_id);

      // Minimum data check for creating a new piece of exercise
      if (circuitObj.label !== undefined) {
        delete circuitObj.newCircuit;
        const requestPayload = { programCircuit: circuitObj };

        return axios
          .put(url, requestPayload)
          .then((res) => {
            if (res.data.error) {
              console.error('Error: ', res.data.error);
            } else {
              ctx.commit('setCreateCircuit', res.data.programCircuit);
              return res.data;
            }
          })
          .catch((err) => {
            console.warn(err);
          });
      } else {
        console.error(
          'circuitObj must contain the following properties "label"',
          circuitObj
        );
      }
    } else {
      console.error(
        `Invalid Params: (circuitObj, trainer_id, org_id, user_id) : (${circuitObj}, ${circuitObj.trainer_id}, ${circuitObj.org_id}, ${circuitObj.user_id})`
      );
    }
  },

  updateCircuit(ctx, { circuitObj }) {
    if (isPayloadValid(circuitObj)) {
      const url = api.URL_UPDATE_CIRCUIT.replace(':circuitID', circuitObj._id)
        .replace(':orgID', circuitObj.org_id)
        .replace(':trainerID', circuitObj.trainer_id)
        .replace(':userID', circuitObj.user_id);

      // Minimum data check for obj
      if (circuitObj.label !== undefined) {
        if (circuitObj.selected) {
          delete circuitObj.selected;
        }
        const requestPayload = { programCircuit: circuitObj };
        return axios
          .post(url, requestPayload)
          .then((res) => {
            ctx.commit('setUpdateCircuitItem', res.data.programCircuit);
            return res.data;
          })
          .catch((err) => {
            console.warn(err);
          });
      } else {
        console.error(
          'circuitObj must contain the following properties to be a valid replacement [trainer_id, org_id, user_id, label, replace]',
          circuitObj
        );
      }
    } else {
      logError(circuitObj);
    }
  },

  deleteCircuit(ctx, { circuitObj }) {
    if (isPayloadValid(circuitObj)) {
      const url = api.URL_DELETE_CIRCUIT.replace(':circuitID', circuitObj._id)
        .replace(':orgID', circuitObj.org_id)
        .replace(':trainerID', circuitObj.trainer_id)
        .replace(':userID', circuitObj.user_id);

      return axios
        .delete(url)
        .then((res) => {
          return res.data;
        })
        .catch((err) => {
          console.warn(err);
        });
    } else {
      logError(circuitObj);
    }
  },

  getCircuitShape(ctx, { orgId, trainerId, userId = 0 }) {
    if (orgId !== undefined && trainerId !== undefined) {
      const url = api.URL_GET_CIRCUIT_SHAPE.replace(':orgID', orgId)
        .replace(':trainerID', trainerId)
        .replace(':userID', userId);

      return axios
        .get(url)
        .then((res) => {
          ctx.commit('setCircuitModel', res.data.programCircuit);
          return res.data;
        })
        .catch((err) => {
          console.warn(err);
        });
    } else {
      console.error(
        `invalid params (orgId, trainerId) : (${orgId}, ${trainerId})`
      );
    }
  },
};

// Private Helpers

function isPayloadValid(obj) {
  // Minimum data check for payload
  return (
    obj !== undefined ||
    (obj.trainer_id !== undefined &&
      obj.org_id !== undefined &&
      obj.user_id !== undefined &&
      obj.exercises.length)
  );
}

function logError(circuitObj) {
  console.error(
    `Invalid Params: (circuitObj, trainer_id, org_id, user_id) : (${circuitObj}, ${circuitObj.trainer_id}, ${circuitObj.org_id}, ${circuitObj.user_id})`
  );
}

function swapExercises(arr, index1, index2) {
  [arr[index1], arr[index2]] = [arr[index2], arr[index1]];
  return arr;
}
